<template>
  <section
    class="hp-news"
    aria-labelledby="latest-news-title"
  >
    <div class="hp-news-container">
      <header class="hp-news-header">
        <div>
          <span class="hp-news-eyebrow"><span aria-hidden="true">🐾</span> {{ copy.eyebrow }}</span>
          <h2 id="latest-news-title">
            {{ copy.title }}
          </h2>
        </div>
        <p>{{ copy.description }}</p>
      </header>

      <div class="hp-news-grid">
        <article
          v-for="(article, index) in articles"
          :key="article.url"
          class="hp-news-card"
          :class="`hp-news-card--${index + 1}`"
        >
          <a
            class="hp-news-image-link"
            :href="article.url"
            :aria-label="`${copy.readArticle}: ${article.title}`"
          >
            <img
              :src="article.featureImage"
              :alt="article.title"
              loading="lazy"
              width="560"
              height="360"
            >
          </a>
          <div class="hp-news-card-body">
            <div class="hp-news-meta">
              <span>{{ article.tag }}</span>
              <time :datetime="article.publishedAt">{{ formatDate(article.publishedAt) }}</time>
            </div>
            <h3>
              <a :href="article.url">{{ article.title }}</a>
            </h3>
            <p>{{ article.excerpt }}</p>
            <a
              class="hp-news-read"
              :href="article.url"
            >
              {{ copy.readArticle }} <span aria-hidden="true">→</span>
            </a>
          </div>
        </article>
      </div>

      <div class="hp-news-footer">
        <a
          class="hp-news-all"
          :href="allNewsUrl"
        >
          {{ copy.exploreAll }} <span aria-hidden="true">↗</span>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  articles: {
    type: Array,
    required: true
  },
  allNewsUrl: {
    type: String,
    required: true
  },
  copy: {
    type: Object,
    required: true
  },
  locale: {
    type: String,
    default: 'en'
  }
})

const formatDate = (date) => new Intl.DateTimeFormat(props.locale, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC'
}).format(new Date(`${date}T00:00:00Z`))
</script>

<style>
.hp-news {
  position: relative;
  overflow: hidden;
  padding: 6.5rem 0 7rem;
  color: #1a1a1a;
  background:
    radial-gradient(circle at 8% 12%, rgba(255, 193, 204, 0.34), transparent 27%),
    radial-gradient(circle at 94% 82%, rgba(163, 196, 243, 0.28), transparent 28%),
    #fffbf0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.hp-news::before,
.hp-news::after {
  position: absolute;
  border-radius: 50%;
  content: '';
  pointer-events: none;
}

.hp-news::before {
  top: 4rem;
  right: 7%;
  width: 34px;
  height: 34px;
  background: rgba(253, 228, 207, 0.9);
  box-shadow: 42px 28px 0 rgba(255, 193, 204, 0.42);
}

.hp-news::after {
  bottom: 4rem;
  left: 4%;
  width: 110px;
  height: 110px;
  border: 1px dashed rgba(255, 139, 167, 0.35);
}

.hp-news-container {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.hp-news-header {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(300px, 0.65fr);
  gap: 3rem;
  align-items: end;
  margin-bottom: 3rem;
}

.hp-news-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0.9rem;
  color: #e66e8b;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hp-news-header h2 {
  margin: 0;
  font-size: clamp(2.4rem, 5vw, 3.75rem);
  font-weight: 900;
  letter-spacing: -0.045em;
  line-height: 1;
}

.hp-news-header p {
  max-width: 500px;
  margin: 0 0 0.2rem;
  color: #666;
  font-size: 1.04rem;
  line-height: 1.7;
}

.hp-news-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.35rem;
}

.hp-news-card {
  display: flex;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(68, 49, 57, 0.08);
  border-radius: 28px;
  box-shadow: 0 16px 50px rgba(93, 66, 76, 0.08);
  transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.hp-news-card:hover {
  transform: translateY(-7px) rotate(-0.25deg);
  box-shadow: 0 26px 60px rgba(93, 66, 76, 0.14);
}

.hp-news-image-link {
  position: relative;
  display: block;
  aspect-ratio: 14 / 9;
  overflow: hidden;
  background: #fff0f5;
}

.hp-news-image-link::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 55%, rgba(31, 23, 29, 0.08));
  content: '';
}

.hp-news-card--2 .hp-news-image-link { background: #edf5ff; }
.hp-news-card--3 .hp-news-image-link { background: #fff3e8; }

.hp-news-image-link img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.hp-news-card:hover .hp-news-image-link img { transform: scale(1.045); }

.hp-news-card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1.45rem 1.5rem 1.6rem;
}

.hp-news-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  color: #777;
  font-size: 0.76rem;
  font-weight: 700;
}

.hp-news-meta > span {
  padding: 0.42rem 0.72rem;
  color: #cc5e79;
  background: #fff0f4;
  border-radius: 999px;
}

.hp-news-card--2 .hp-news-meta > span { color: #527fc0; background: #edf5ff; }
.hp-news-card--3 .hp-news-meta > span { color: #b46f35; background: #fff3e8; }

.hp-news-card h3 {
  margin: 1.15rem 0 0.75rem;
  font-size: 1.32rem;
  line-height: 1.25;
  letter-spacing: -0.025em;
}

.hp-news-card h3 a,
.hp-news-read,
.hp-news-all {
  color: inherit;
  text-decoration: none;
}

.hp-news-card p {
  margin: 0;
  color: #666;
  font-size: 0.92rem;
  line-height: 1.65;
}

.hp-news-read {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  align-self: flex-start;
  margin-top: auto;
  padding-top: 1.4rem;
  color: #d9637f;
  font-size: 0.9rem;
  font-weight: 800;
}

.hp-news-read span { transition: transform 0.2s ease; }
.hp-news-read:hover span { transform: translateX(4px); }

.hp-news-footer {
  display: flex;
  justify-content: center;
  margin-top: 2.6rem;
}

.hp-news-all {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.25rem;
  background: #1a1a1a;
  color: #fff;
  border-radius: 999px;
  font-size: 0.92rem;
  font-weight: 800;
  transition: transform 0.2s ease, background 0.2s ease;
}

.hp-news-all:hover { transform: translateY(-3px); background: #e66e8b; }

.hp-news a:focus-visible {
  outline: 3px solid #ff8ba7;
  outline-offset: 4px;
}

@media (max-width: 960px) {
  .hp-news-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hp-news-card:last-child { grid-column: 1 / -1; max-width: calc(50% - 0.675rem); justify-self: center; }
}

@media (max-width: 720px) {
  .hp-news { padding: 5rem 0; }
  .hp-news-header { grid-template-columns: 1fr; gap: 1.25rem; }
  .hp-news-grid { grid-template-columns: 1fr; }
  .hp-news-card:last-child { grid-column: auto; max-width: none; }
  .hp-news-header h2 { font-size: 2.5rem; }
}

@media (prefers-reduced-motion: reduce) {
  .hp-news-card,
  .hp-news-image-link img,
  .hp-news-read span,
  .hp-news-all { transition: none; }
  .hp-news-card:hover,
  .hp-news-all:hover { transform: none; }
}
</style>
